'use client';

import React from 'react';
import { useSelector } from 'react-redux';

import Link from 'next/link';

import Contacts from '@/components/elements/contacts';
import { useTranslation } from '@/i18n/client';
import { selectIsLoggedIn, selectUserName } from '@/store/auth';
import { buildUrl } from '@/utils/navigation';
import { createTestId } from '@/utils/string-utils';

import { ScrollLink } from '../elements/scroll-link';
import { HeaderPopupKeys, HeaderProps } from './header.types';
import SwitcherLanguage from './switcher-language';

const createrHeaderTestId = ({ name }: { name: string }) => createTestId({ component: 'header', name });

export default function HeaderDesktop(props: HeaderProps) {
    const { t: tCo } = useTranslation(props.lang, 'common');
    const { t: tAu } = useTranslation(props.lang, 'auth');

    const isLoggedIn = useSelector(selectIsLoggedIn);
    const userName = useSelector(selectUserName);

    const country = props.country?.slug || 'serbia';

    return (
        <>
            <nav className="flex flex-1 gap-x-2">
                {props.isCalendar ? (
                    <ScrollLink
                        data-testid={createrHeaderTestId({ name: 'home-link' })}
                        className="text-regular whitespace-nowrap"
                        prefetch={false}
                        onClick={() => props.onClose(HeaderPopupKeys.auth)}
                        href={buildUrl('', { children: [country], fragment: 'events' })}
                        smooth={true}
                        scroll={false}
                    >
                        {tCo('events')}
                    </ScrollLink>
                ) : (
                    <Link
                        data-testid={createrHeaderTestId({ name: 'home-link' })}
                        className="text-regular whitespace-nowrap"
                        prefetch={false}
                        href={buildUrl('', { children: [country] })}
                        scroll={false}
                    >
                        {tCo('events')}
                    </Link>
                )}
                <Link
                    data-testid={createrHeaderTestId({ name: 'about-link' })}
                    className="text-regular whitespace-nowrap"
                    prefetch={false}
                    href={buildUrl('about', { lang: props.lang })}
                    scroll={false}
                >
                    {tCo('about')}
                </Link>
                <Link
                    prefetch={false}
                    data-testid={createrHeaderTestId({ name: 'collaboration-link' })}
                    className="text-regular whitespace-nowrap"
                    href={buildUrl('collaboration', { lang: props.lang })}
                    scroll={false}
                >
                    {tCo('collaboration')}
                </Link>
                {props.country?.slug !== 'russia' && (
                    <Link
                        data-testid={createrHeaderTestId({ name: 'services-link' })}
                        className="text-regular whitespace-nowrap"
                        prefetch={false}
                        href={buildUrl('', { children: [country, 'services'] })}
                        scroll={false}
                    >
                        {tCo('pServices')}
                    </Link>
                )}
            </nav>
            <div className="grid grid-flow-col justify-center gap-4">
                <nav className="my-auto flex gap-2">
                    <Contacts country={props.country?.code} size={4} is_header={true}></Contacts>
                </nav>

                {props.country?.slug !== 'russia' && <SwitcherLanguage lang={props.lang} />}
                {isLoggedIn && (
                    <>
                        <Link
                            data-testid={createrHeaderTestId({ name: 'user-cabinet-link' })}
                            href={buildUrl('user', { lang: props.lang })}
                            className="text text-base"
                        >
                            <div className="border-regular flex cursor-pointer items-center justify-between gap-x-4 rounded-full px-5 py-2">
                                {userName && (
                                    <div className="h-6 w-6 rounded-full bg-accent text-center text-white">
                                        <span className="m-auto block capitalize text-white">
                                            {userName.slice(0, 1) || ' '}
                                        </span>
                                    </div>
                                )}
                                {tAu('buttons.profile')}
                            </div>
                        </Link>
                    </>
                )}

                {props.isVisible?.auth ? (
                    <div className="square-button" onClick={() => props.onClose(HeaderPopupKeys.auth)}>
                        <span className={`icon ${props.isVisible.auth ? 'bg-close' : 'bg-menu'} `}></span>
                    </div>
                ) : (
                    !isLoggedIn && (
                        <button
                            data-testid={createrHeaderTestId({ name: 'auth-btn' })}
                            onClick={() => props.onOpen(HeaderPopupKeys.auth)}
                            className="btn-primary border-solid"
                        >
                            {tAu('buttons.loginShort')}
                        </button>
                    )
                )}
            </div>
        </>
    );
}
